import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getStudioV2NodeMeta } from '../studioV2NodeCatalog';
import { flowThumbnail } from './flowBuilderModel';

const W = 280;
const H = 120;
const PAD = 16;
const NODE_W = 34;
const NODE_H = 14;

/** Tiny read-only sketch of a flow's steps and connections, drawn from saved node positions. */
export default function FlowMiniDiagram({ definition, height = H }) {
  const theme = useTheme();
  const { nodes, edges } = useMemo(() => flowThumbnail(definition), [definition]);
  const colorOf = (type) => {
    const key = getStudioV2NodeMeta(type).colorKey;
    if (key === 'grey') return theme.palette.grey[500];
    return theme.palette[key]?.main || theme.palette.primary.main;
  };
  const px = (n) => ({
    x: PAD + n.x * (W - PAD * 2 - NODE_W),
    y: PAD + n.y * (H - PAD * 2 - NODE_H),
  });

  return (
    <Box
      sx={{
        height,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, 0.08)} 1px, transparent 1px)`,
        backgroundSize: '12px 12px',
        overflow: 'hidden',
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Flow diagram">
        {edges.map(([a, b]) => {
          const p = px(nodes[a]);
          const q = px(nodes[b]);
          const x1 = p.x + NODE_W / 2;
          const y1 = p.y + NODE_H;
          const x2 = q.x + NODE_W / 2;
          const y2 = q.y;
          const my = (y1 + y2) / 2;
          return (
            <path
              key={`${a}-${b}`}
              d={`M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`}
              fill="none"
              stroke={alpha(theme.palette.text.primary, 0.28)}
              strokeWidth={1.25}
            />
          );
        })}
        {nodes.map((n) => {
          const p = px(n);
          const c = colorOf(n.type);
          return (
            <rect
              key={n.id}
              x={p.x}
              y={p.y}
              width={NODE_W}
              height={NODE_H}
              rx={4}
              fill={alpha(c, 0.18)}
              stroke={c}
              strokeWidth={1.25}
            />
          );
        })}
      </svg>
    </Box>
  );
}
